import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { AccountAttributes } from '@loyalty/shared-common/gamification-api-client';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IsLoading, useLoaders } from '../shared/context/context-utils';
import { LoginScreen } from '../auth/components/LoginScreen';
import { useApi } from '../shared/api-client/ApiClientProvider';
import { useSelectiveEffect } from '../shared/use-selective-effect';

interface AccountsContextInterface {
  accounts: AccountAttributes[];
  currentAccount: AccountAttributes;
  setCurrentAccount: (account: AccountAttributes) => void;
  isLoading: IsLoading;
  fetchAccounts: () => void;
}

export const AccountsContext = createContext<AccountsContextInterface>({} as any);

export function useAccounts() {
  return useContext(AccountsContext);
}

export function AccountsProvider({ children }: { children: ReactNode }) {
  const [accounts, setAccounts] = useState<AccountAttributes[]>([]);
  const [currentAccount, setCurrentAccount] = useState<AccountAttributes>(undefined as any);
  const { gamificationApi } = useApi();
  const { addLoader, removeLoader, isLoading } = useLoaders();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const locationAccountId = useMemo(() => location.pathname.split('/')?.[2], [location.pathname]);

  const getAccount = useCallback(
    (accountId: string) => {
      return accounts.find(({ id }) => id === accountId);
    },
    [accounts],
  );

  const fetchAccounts = useCallback(() => {
    addLoader('fetchAccounts');

    gamificationApi
      .getAllAccounts()
      .then(accounts => {
        setAccounts(accounts);
        if (!accounts.length) {
          return;
        }
        let account;
        account = accounts.find(({ id }) => id === locationAccountId);

        if (!account) {
          account = accounts[0];
        }

        setCurrentAccount(account);
      })
      .catch(() => setAccounts([]))
      .finally(() => removeLoader('fetchAccounts'));
  }, [addLoader, removeLoader, gamificationApi, location.pathname]);

  useSelectiveEffect(
    () => {
      fetchAccounts();
    },
    [fetchAccounts],
    [],
  );

  useEffect(() => {
    if (currentAccount) {
      if (currentAccount.id !== locationAccountId) {
        const account = getAccount(locationAccountId);
        if (account) {
          setCurrentAccount(account);
        } else {
          navigate(`/accounts/${currentAccount.id}/programs`);
        }
      }
    }
  }, [location.pathname, currentAccount]);

  const context = {
    accounts,
    currentAccount,
    setCurrentAccount,
    isLoading,
    fetchAccounts,
  };

  return isLoading('fetchAccounts') ? (
    <LoginScreen loading={true} />
  ) : (
    <AccountsContext.Provider value={context}>{children}</AccountsContext.Provider>
  );
}
